/**
 * Demo-Modus Context
 * 
 * Verwaltet den Demo-Status:
 * - Prüft serverseitig ob die Session eine Demo-Session ist
 * - Timer läuft serverseitig (JWT-Ablauf), Frontend zeigt nur den Countdown
 * - Bei Ablauf: Overlay mit CTA → /termin
 * - Blendet Preise/Stripe-Buttons aus
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";

interface DemoState {
  isDemo: boolean;
  expired: boolean;
  remainingSeconds: number;
  demoExpiresAt: number;
}

interface DemoContextType {
  isDemo: boolean;
  expired: boolean;
  remainingSeconds: number;
  /** Formatted time string MM:SS */
  timeDisplay: string;
  /** Check demo status from server */
  checkStatus: () => Promise<void>;
}

const DemoContext = createContext<DemoContextType>({
  isDemo: false,
  expired: false,
  remainingSeconds: 0,
  timeDisplay: "00:00",
  checkStatus: async () => {},
});

export function DemoProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<DemoState>({
    isDemo: false,
    expired: false,
    remainingSeconds: 0,
    demoExpiresAt: 0,
  });

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasChecked = useRef(false);

  const checkStatus = useCallback(async () => {
    try {
      const res = await fetch("/api/demo/status", { credentials: "include" });
      const data = await res.json();

      if (data.isDemo) {
        setState({
          isDemo: true,
          expired: data.expired || false,
          remainingSeconds: data.remainingSeconds || 0,
          demoExpiresAt: data.demoExpiresAt || 0,
        });
      } else {
        setState({ isDemo: false, expired: false, remainingSeconds: 0, demoExpiresAt: 0 });
      }
    } catch {
      // Silently fail — not a demo session
    }
  }, []);

  // Check on mount
  useEffect(() => {
    if (!hasChecked.current) {
      hasChecked.current = true;
      checkStatus();
    }
  }, [checkStatus]);

  // Client-side countdown (synced with server expiresAt)
  useEffect(() => {
    if (!state.isDemo || state.expired || !state.demoExpiresAt) return;

    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((state.demoExpiresAt - Date.now()) / 1000));

      if (remaining <= 0) {
        setState((prev) => ({ ...prev, expired: true, remainingSeconds: 0 }));
        if (intervalRef.current) clearInterval(intervalRef.current);
      } else {
        setState((prev) => ({ ...prev, remainingSeconds: remaining }));
      }
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [state.isDemo, state.expired, state.demoExpiresAt]);

  const timeDisplay = (() => {
    const mins = Math.floor(state.remainingSeconds / 60);
    const secs = state.remainingSeconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  })();

  return (
    <DemoContext.Provider
      value={{
        isDemo: state.isDemo,
        expired: state.expired,
        remainingSeconds: state.remainingSeconds,
        timeDisplay,
        checkStatus,
      }}
    >
      {children}
    </DemoContext.Provider>
  );
}

export function useDemo() {
  return useContext(DemoContext);
}
